"use client";

import { ArrowLeft, Category2, SearchNormal1 } from "iconsax-reactjs";
import { useLocale, useTranslations } from "next-intl";
import type React from "react";
import { Link } from "@/i18n/navigation";
import { localizeName } from "@/lib/i18n";

type Product = {
  id: string;
  title: string;
  category_id?: string | null;
  category_name:
    | { fa?: string | null; en?: string | null }
    | Record<string, never>
    | null;
};

interface ProductSearchResultsProps {
  products: Product[] | Product[][] | undefined;
  searchValue: string;
  onResultClick: () => void;
}

// Helper function to build product redirect URL
const getProductUrl = (categoryId?: string, productTitle?: string) => {
  const params = new URLSearchParams();
  if (categoryId) {
    params.set("category_id", categoryId);
  }
  if (productTitle) {
    params.set("q", productTitle);
  }
  const queryString = params.toString();
  return `/search${queryString ? `?${queryString}` : ""}`;
};

// Flatten nested arrays - API types show nested arrays but actual response may be flat
const flattenArray = <T,>(arr: T[] | T[][] | undefined): T[] => {
  if (!arr || arr.length === 0) return [];
  if (Array.isArray(arr[0])) {
    return (arr as T[][]).flat(2) as T[];
  }
  return arr as T[];
};

export const ProductSearchResults: React.FC<ProductSearchResultsProps> = ({
  products,
  searchValue,
  onResultClick,
}) => {
  const t = useTranslations("nav.search");
  const locale = useLocale();
  const allProducts = flattenArray<Product>(
    products as unknown as Product[] | Product[][],
  );

  // Separate products by whether they have a category
  const productsWithCategory = allProducts.filter((p) => p.category_id);
  const productsWithoutCategory = allProducts.filter((p) => !p.category_id);

  const hasProducts = allProducts.length > 0;

  if (!hasProducts) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-sm text-text-secondary">{t("noResults")}</p>
      </div>
    );
  }

  return (
    <>
      <div className="py-2 px-5 gap-5 flex items-center mt-6">
        <Category2 size={20} />
        <p className="text-sm font-medium">
          {t("allItemsFor", { query: searchValue })}
        </p>
      </div>

      <div className="px-4 space-y-0">
        {/* Products with category - navigates with category_id + search filter */}
        {productsWithCategory.map((product) => (
          <Link
            key={product.id}
            href={getProductUrl(
              product.category_id || undefined,
              product.title,
            )}
            className="flex flex-col py-2 hover:bg-bg-secondary/50 transition-colors"
            onClick={onResultClick}
          >
            <div className="flex items-center gap-4">
              <SearchNormal1 size={20} />
              <div className="flex flex-col">
                <span className="text-sm font-medium">{product.title}</span>
                {localizeName(product.category_name, locale) && (
                  <span className="text-xs font-medium">
                    {t("inCategory")}{" "}
                    <span className="text-semantic-link font-semibold">
                      {localizeName(product.category_name, locale)}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </Link>
        ))}

        {/* Separator between products with and without category */}
        {productsWithCategory.length > 0 &&
          productsWithoutCategory.length > 0 && (
            <div className="border-b border-stroke-subtle my-2" />
          )}

        {/* Products without category - navigates with search filter only */}
        {productsWithoutCategory.map((product) => (
          <Link
            key={product.id}
            href={getProductUrl(undefined, product.title)}
            className="flex items-center justify-between py-3 border-b border-stroke-subtle last:border-b-0 hover:bg-bg-secondary/50 transition-colors"
            onClick={onResultClick}
          >
            <span className="text-sm font-medium truncate max-w-30">
              {product.title}
            </span>
            <ArrowLeft
              size={24}
              className="text-text-secondary shrink-0"
              style={{ transform: "rotate(135deg)" }}
            />
          </Link>
        ))}
      </div>
    </>
  );
};
